import { useState } from 'react';
import { formatPhoneNumber } from '@stitch/pbx-adapter/catalog';
import { smartApi } from '../../api/client';
import { useAuth } from '../../lib/auth';
import { useData } from '../../lib/useData';
import { Button, Card, Field, LoadError, Loading, Notice } from '../ui';

/**
 * Company Settings → Export messages (eDiscovery): the company's own admins choose people and/or
 * conversations and the days to cover, and download a zip (JSON lines, a spreadsheet and what it
 * covers). Stitch staff don't see it: messages are private to the company.
 */

const day = (d: Date) => d.toISOString().slice(0, 10);

export function MessagesExportCard({ domain, companyName }: { domain: string; companyName: string }) {
  const { state } = useAuth();
  const mine = state?.memberships?.find((m) => m.tenant === domain.toLowerCase());
  if (!mine || mine.role !== 'admin') return null;
  return <ExportForm domain={domain} companyName={companyName} />;
}

function ExportForm({ domain, companyName }: { domain: string; companyName: string }) {
  const targets = useData(() => smartApi.holdTargets(domain), [domain], { live: false });
  const [people, setPeople] = useState<string[]>([]);
  const [conversations, setConversations] = useState<string[]>([]);
  const [from, setFrom] = useState(() => day(new Date(Date.now() - 30 * 24 * 3600_000)));
  const [to, setTo] = useState(() => day(new Date()));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const [error, setError] = useState('');
  const t = targets.data;
  if (targets.error)
    return (
      <Card title="Export messages">
        <LoadError error={targets.error} retry={targets.reload} />
      </Card>
    );
  if (!t)
    return (
      <Card title="Export messages">
        <Loading label="Loading people and conversations" />
      </Card>
    );
  const toggle = (list: string[], set: (v: string[]) => void, id: string) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const ready = (people.length > 0 || conversations.length > 0) && /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to;

  async function download() {
    setBusy(true);
    setError('');
    setDone('');
    try {
      const { blob, name } = await smartApi.exportMessages(domain, { people, conversations, from, to });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setDone(`Downloaded ${name}. It shows under Activity: who exported it, what it covered and how many messages, never what they said.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The export didn’t download.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Export messages">
      <div className="card-body settings-section">
        <p className="muted settings-lede">
          For a legal or compliance request (eDiscovery): download {companyName}’s messages for the people and conversations you choose, over the days you choose. Channels
          and texts are included; direct messages only for people or conversations under a legal hold. Messages edited or deleted under a hold come with what they said
          before. The zip holds a spreadsheet (CSV) and the same as JSON lines.
        </p>
        <fieldset className="plain-fieldset smart-group">
          <legend>People</legend>
          <div className="smart-picks">
            {t.people.map((p) => (
              <label key={p.id} className="check-row">
                <input type="checkbox" checked={people.includes(p.id)} onChange={() => toggle(people, setPeople, p.id)} />
                <span className="cell-stack">
                  <span>{p.name}</span>
                  <small className="muted">{p.email}</small>
                </span>
              </label>
            ))}
          </div>
          <small className="muted">The channels they’re in or wrote in, and the text conversations they wrote in.</small>
        </fieldset>
        {t.conversations.length > 0 && (
          <fieldset className="plain-fieldset smart-group">
            <legend>Conversations</legend>
            <div className="smart-picks">
              {t.conversations.map((c) => (
                <label key={c.id} className="check-row">
                  <input type="checkbox" checked={conversations.includes(c.id)} onChange={() => toggle(conversations, setConversations, c.id)} />
                  <span>{c.kind === 'channel' ? `#${c.name ?? ''}` : `Texts with ${formatPhoneNumber(c.number ?? '')}`}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <div className="form-grid smart-dates">
          <Field label="From" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          <Field label="To" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} hint="Whole days, in UTC." />
        </div>
        {done && <Notice tone="ok">{done}</Notice>}
        {error && <Notice tone="crit">{error}</Notice>}
        <div className="settings-actions">
          <Button variant="primary" icon="download" disabled={busy || !ready} onClick={() => void download()}>
            {busy ? 'Preparing…' : 'Download export'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
